import { ToolbarButton, ToolbarGroup } from '@/components/ui/Toolbar'
import { CheckIcon, HistoryIcon, PencilIcon, Trash2Icon } from 'lucide-react'

import { Command } from '@/shared/commands/command.types'
import {
  executeCommand,
  isCommandEnabled,
  isCommandVisible,
} from '@/shared/commands/command.utils'

import { CellToolbarTooltip } from '@/features/rundown/cell/toolbar/CellToolbar.tooltip'

import {
  CellCommandContext,
  cellCommands,
  useCellCommandContext,
} from '../cell.commands'

export function CellToolbarItems({
  items,
}: {
  items: Command<CellCommandContext>[]
}) {
  const context = useCellCommandContext()

  return (
    // Only one group for now
    <ToolbarGroup>
      {items
        .filter((command) => isCommandVisible(command, context))
        .map((command) => (
          <CellToolbarItem
            key={command.id}
            command={command}
            context={context}
          />
        ))}
    </ToolbarGroup>
  )
}

function CellToolbarItem({
  command,
  context,
}: {
  command: Command<CellCommandContext>
  context: CellCommandContext
}) {
  const isDisabled = !isCommandEnabled(command, context)

  return (
    <CellToolbarTooltip
      label={command.label}
      shortcut={command.shortcuts}
    >
      <ToolbarButton
        aria-label={command.label}
        disabled={isDisabled}
        onClick={() => executeCommand(command, context)}
      >
        <CellToolbarIconWrap isDisabled={isDisabled}>
          {getToolbarItemIcon(command)}
        </CellToolbarIconWrap>
      </ToolbarButton>
    </CellToolbarTooltip>
  )
}

/**
 * Helper that maps commands to Lucide icons. Icons are currently not used for
 * commands outside toolbars, so the command does not store icon information.
 */
function getToolbarItemIcon(command: Command<CellCommandContext>) {
  switch (command) {
    case cellCommands.edit:
      return <PencilIcon />

    case cellCommands.history:
      return <HistoryIcon />

    case cellCommands.discard:
      return <Trash2Icon />

    default:
      if (command.id === 'cell.save') {
        return <CheckIcon />
      }
      throw new Error('Unsupported command')
  }
}

/**
 * Takes a Lucide icon as children, wraps it with custom "off"-state slash.
 *
 * Only a handful of Lucide icons come with an "off" variant, so this wrapper
 * creates a similar effect similar with CSS only, for consistency.
 */
function CellToolbarIconWrap({
  children,
  isDisabled,
}: React.ComponentProps<'span'> & {
  isDisabled: boolean
}) {
  return (
    <span className="inline-grid place-items-center grid-stack">
      {/* Lucide icon */}
      {children}

      {/* Slash overlay */}
      {isDisabled && (
        <span
          aria-hidden="true"
          className="h-[1.5px] w-[120%] rotate-45 bg-current outline-2 outline-popover"
        />
      )}
    </span>
  )
}
